import { DownOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, { FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { Language, Product } from 'types/schema';
import formatMoney from 'utils/formatMoney';
import './ProductPrice.css';

export const PriceContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const PriceFlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0 4px;
`;

const DiscountPrice = styled.p`
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #000;
`;

const BasePrice = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #aaacb2;
  text-decoration: line-through;
  line-height: 26px;
`;

const PerPrice = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
`;

const StyledDownIcon = styled(DownOutlined)`
  font-size: 10px;
  margin-left: 2px;
  cursor: pointer;
`;

const TooltipList = styled.ul`
  padding: 0;
`;

interface IProductPrice {
  product: Product;
  baseUom: string;
  netUom: string;
  lang: Language;
}

interface TooltipContentProps {
  uom: string;
  baseUomPrice: number | null | undefined;
  netUomPrice: number | null | undefined;
  netUom: string;
  lang: Language;
}

interface TooltipUnitPriceProps extends Omit<TooltipContentProps, 'uom'> {
  baseUom: string;
}

interface UnitPriceDisplayProps {
  price: number | null | undefined;
  unit: string;
}

const PRICE_TEXT = {
  [Language.Vi]: 'Giá',
  [Language.En]: 'Price',
};

const formatPrice = (price: number | null | undefined): string => {
  return `${formatMoney(price || 0)}đ`;
};

const formatUnitPrice = (price: number | null | undefined, unit?: string): string => {
  return `${formatPrice(price)}${unit ? `/${unit}` : ''}`;
};

const TooltipContent = ({ uom, baseUomPrice, netUom, netUomPrice, lang }: TooltipContentProps) => (
  <TooltipList>
    <li>
      {PRICE_TEXT[lang]}/{uom}: {formatUnitPrice(baseUomPrice, uom)}
    </li>
    {netUomPrice && (
      <li>
        {PRICE_TEXT[lang]}/{netUom}: {formatUnitPrice(netUomPrice, netUom)}
      </li>
    )}
  </TooltipList>
);

const UnitPriceDisplay = ({ price, unit }: UnitPriceDisplayProps) => (
  <PerPrice>({formatUnitPrice(price, unit)})</PerPrice>
);

const TooltipUnitPrice = ({ baseUomPrice, baseUom, netUomPrice, netUom, lang }: TooltipUnitPriceProps) => {
  const tooltipProps = {
    uom: baseUom,
    baseUomPrice: baseUomPrice,
    netUom: netUom,
    netUomPrice: netUomPrice,
    lang: lang,
  };

  return (
    <Tooltip
      overlayClassName="price-tooltip-container"
      arrow={false}
      placement="bottomLeft"
      title={<TooltipContent {...tooltipProps} />}
    >
      <PerPrice>
        ({formatUnitPrice(baseUomPrice, baseUom)}
        <StyledDownIcon />)
      </PerPrice>
    </Tooltip>
  );
};

const ProductPrice: FC<IProductPrice> = ({ product, baseUom, netUom, lang }) => {
  const { price, originalPrice, baseUomPrice, netUomPrice, netUnit, baseUnit } = product;

  const renderUomPrice = useMemo(() => {
    if (baseUnit === 1 && netUnit !== 1 && netUomPrice && netUom) {
      return <UnitPriceDisplay price={netUomPrice} unit={netUom} />;
    }

    if (baseUomPrice) {
      return netUomPrice && netUom !== baseUom && baseUnit !== 1 ? (
        <TooltipUnitPrice
          baseUomPrice={baseUomPrice}
          baseUom={baseUom}
          netUomPrice={netUomPrice}
          netUom={netUom}
          lang={lang}
        />
      ) : baseUnit !== 1 || netUnit !== 1 ? (
        <UnitPriceDisplay price={baseUomPrice} unit={baseUom} />
      ) : null;
    }

    return null;
  }, [baseUnit, netUnit, netUomPrice, baseUomPrice, netUom, baseUom, lang]);

  return (
    <PriceContainer>
      <PriceFlexColumn>
        <PriceRow>
          <DiscountPrice>
            {formatMoney(price)}
            <span style={{ fontSize: 20 }}>đ</span>
          </DiscountPrice>
          {originalPrice > price && <BasePrice>{formatPrice(originalPrice)}</BasePrice>}
        </PriceRow>

        {renderUomPrice}
      </PriceFlexColumn>
    </PriceContainer>
  );
};

export default memo(ProductPrice);
